import { useCallback, useEffect, useRef, useState } from 'react'
import { initRxDB } from '../../index'
import {  categorizeSyncError } from '../../utils/syncErrorUtils'
import type {CategorizedSyncError} from '../../utils/syncErrorUtils';
import type { MolyDatabase } from '../../index'
import useLoginStore from '@/store/LoginStore'

type RxDBState = {
  db: MolyDatabase | null
  isInitializing: boolean
  isSyncing: boolean
  error: Error | null
  categorizedError: CategorizedSyncError | null
}

const INITIAL_STATE: RxDBState = {
  db: null,
  isInitializing: true,
  isSyncing: false,
  error: null,
  categorizedError: null,
}

/**
 * Hook to initialize and access the scoped RxDB database.
 * Handles rapid environment switches by cancelling stale in-flight initializations.
 */
export const useRxDB = () => {
  const [state, setState] = useState<RxDBState>(INITIAL_STATE)
  const [retryCount, setRetryCount] = useState(0)
  // envId maps to workspaceId from LoginStore
  const envId = useLoginStore((s) => s.workspaceId)
  const userId = useLoginStore((s) => s.userId)
  const initTokenRef = useRef(0)

  useEffect(() => {

    if (!envId || !userId) {
      setState((prev) => ({
        ...prev,
        db: null,
        isInitializing: false,
        isSyncing: false,
        error: null,
        categorizedError: null,
      }))
      return
    }

    let cancelled = false
    const token = ++initTokenRef.current

    setState((prev) => ({
      ...prev,
      isInitializing: true,
      isSyncing: true,
      error: null,
      categorizedError: null,
    }))

    ;(async () => {
      try {
        const database = await initRxDB(envId, userId)
        if (cancelled || token !== initTokenRef.current) {
          return
        }

        setState({
          db: database,
          isInitializing: false,
          isSyncing: false,
          error: null,
          categorizedError: null,
        })
      } catch (err) {
        // Error handling with categorization
        if (cancelled || token !== initTokenRef.current) return

        const error = err as Error
        const categorized = categorizeSyncError(error)

        setState({
          db: null,
          isInitializing: false,
          isSyncing: false,
          error,
          categorizedError: categorized,
        })
      }
    })()

    return () => {
      // Only set cancelled flag - don't close database here
      // The database will be closed by the next initRxDB call if needed
      cancelled = true
    }
  }, [envId, userId, retryCount])

  const reset = useCallback(() => {
    initTokenRef.current += 1
    setState({ ...INITIAL_STATE })
    setRetryCount((n) => n + 1)
  }, [])

  return {
    ...state,
    isReady: Boolean(state.db) && !state.isInitializing && !state.error,
    reset,
  }
}
